import { Box, Flex, Text } from "@chakra-ui/react";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { CustomText } from "@root/components/common/components";
import useSidebar from "@root/hooks/useSidebar";
import { isRoutePrefixMatch } from "@root/utils/helpers";
import { NavControl } from "./navControl";
import { SidebarHeader } from "./sidebarHeader";
import { StyledSidebarContainer, StyledSidebarSubBox } from "./style";
import { CustomPopup } from "@root/components/common/modals/CustomPopup";
import { DeletePopupContent } from "@root/pages/components";
import { useEffect, useState } from "react";
import useChatHistory from "@root/hooks/useChatHistory";
import { useSelector } from "react-redux";
import { ChatHistorySidebar } from "@root/pages/chat-history/ChatHistorySidebar";
import { useGlobalContext } from "@root/context/global-context";
import { CollapseIcon } from "@root/assets/icons/collapseicon";
import { LogoutModal } from "@root/components/logout/logout";
import { getLoggedInUser } from "@root/store/selectors/entities";
import { USER_TYPES } from "@root/constants";
import { LanguageToggle } from "./languagetoggler";
import { useTourGuideContext } from "@root/context/tour-guide-context";
import { useTourGuideHover } from "@root/hooks/useTourGuideHover";
import { TourGuideHoverTooltip } from "@root/components/tour-guide/TourGuideHoverTooltip";

export function Sidebar() {
  const {
    isDarkTheme,
    lg,
    sidebar,
    pathname,
    languages,
    menuItemsList,
    profileData,
    logoutPopup,
    setLogoutPopup,
    deleteAccountPopup,
    setDeleteAccountPopup,
    logoutHandler,
    deleteUserAccount,
    loading,
    t,
    isMenuOpen,
    setIsMenuOpen,
    updatePasswordOpen,
    setUpdatePasswordOpen,
    collapsedSections,
    toggleSection,
  } = useSidebar();

  const {
    threads,
    handleChatClick,
    loading: chatHistoryLoading,
    refreshThreads,
  } = useChatHistory();
  const { isSidebarOpen, setIsSidebarOpen } = useGlobalContext();
  const isLoggedInUser: any = useSelector(getLoggedInUser);
  const { highlightedItemKey, hoverTooltipsEnabled } = useTourGuideContext();

  const [selectedItemKey, setSelectedItemKey] = useState<string | null>(null);
  const [isConversationHistoryCollapsed, setIsConversationHistoryCollapsed] =
    useState<boolean>(true);

  // Sync selectedItemKey with pathname
  useEffect(() => {
    const activeItem = sidebar?.find((menu: any) => {
      if (!menu?.routes) return false;
      // Use prefix matching for sidebar items
      return isRoutePrefixMatch(pathname, menu.routes);
    });
    if (activeItem) {
      setSelectedItemKey(activeItem.key);
    }
  }, [pathname, sidebar]);

  const toggleSidebar = () => {
    setIsSidebarOpen((prev: any) => !prev);
  };

  // Get hover tooltip data for legal chatbot sidebar item (defined outside component to avoid hook issues)
  // Disabled for now - will enable later with better content
  const getLegalChatbotStep = (menuKey: string) => {
    // Disabled hover tooltip for legal chatbot sidebar item
    return null;
  };

  const SidebarItem = ({ menu }: any) => {
    // Always call hooks first, before any early returns
    const legalChatbotStep = getLegalChatbotStep(menu?.key);
    const hoverHooks = useTourGuideHover(
      menu?.key === "legalChatbot" ? "sidebar-legal" : `${menu?.key}-hover`,
      legalChatbotStep
    );

    // If it's a heading, render it differently
    if (menu?.isHeading) {
      const isCollapsed = collapsedSections?.[menu?.section];
      return (
        <Flex flexDir={"column"} userSelect={"none"}>
          {isSidebarOpen && (
            <Flex
              alignItems={"center"}
              gap={"8px"}
              py={2}
              mt={1}
              mb={1}
              px={"8px"}
              cursor={"pointer"}
              onClick={() => toggleSection(menu?.section)}
              justifyContent={"space-between"}
            >
              <CustomText
                fontSize={"14px"}
                fontWeight={600}
                textColor={"#757575"}
                text={menu?.text}
                sidebar={true}
              />
              <Box
                as="svg"
                width="16px"
                height="16px"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#757575"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                transform={isCollapsed ? "rotate(180deg)" : "rotate(0deg)"}
                transition="transform 0.2s ease"
              >
                <polyline points="6 9 12 15 18 9" />
              </Box>
            </Flex>
          )}
        </Flex>
      );
    }

    // Regular sidebar item rendering (your existing code)
    const isHighlighted = highlightedItemKey === menu?.key;

    return (
      <Flex flexDir={"column"} userSelect={"none"}>
        <Flex
          alignItems={"center"}
          width={isSidebarOpen ? "100%" : "40px"}
          height={isSidebarOpen ? "auto" : "40px"}
          borderRadius={isSidebarOpen ? "8px" : "50%"}
          justifyContent={isSidebarOpen ? "space-between" : "center"}
          px={isSidebarOpen ? "8px" : "0px"}
          py={isSidebarOpen ? "8px" : "0px"}
          cursor={"pointer"}
          onClick={() => {
            setSelectedItemKey(menu?.key);
            menu?.onClick();
          }}
          {...hoverHooks.hoverProps}
          _hover={{
            bg: "#EBEBEB",
          }}
          bg={
            selectedItemKey === menu?.key ||
              (menu?.routes && isRoutePrefixMatch(pathname, menu?.routes))
              ? "#EBEBEB"
              : "transparent"
          }
          border={isHighlighted ? "3px solid white" : "none"}
          boxShadow={isHighlighted ? "0 0 0 2px rgba(0, 0, 0, 0.1)" : "none"}
          position="relative"
          zIndex={isHighlighted ? 10001 : "auto"}
          data-tour-item={
            menu?.key === "projectDocumentChatbot"
              ? "projectDocumentChatbot"
              : menu?.key === "permitReview"
                ? "permitReview"
                : menu?.key === "legalChatbot"
                  ? "legalChatbot"
                  : menu?.key === "materialComparisonNested"
                    ? "materialComparisonNested"
                    : menu?.key
          }
        >
          <Flex alignItems={"center"} gap={isSidebarOpen ? "14px" : "0px"}>
            {menu?.iconComponent ? (
              <Box color="#000000">
                <menu.iconComponent color={"#000000"} fill={"#000000"} />
              </Box>
            ) : selectedItemKey === menu?.key ? (
              menu?.activeIcon
            ) : (
              menu?.icon
            )}
            {isSidebarOpen && (
              <CustomText
                fontSize={"14px"}
                marginRight={"30px"}
                fontWeight={500}
                textColor={"#000000"}
                text={menu?.text}
                sidebar={true}
              />
            )}
          </Flex>
        </Flex>
        {/* Hover tooltip for legal chatbot sidebar item */}
        {hoverHooks.isHovered && hoverHooks.tooltipData && (
          <TourGuideHoverTooltip
            title={hoverHooks.tooltipData.title}
            description={hoverHooks.tooltipData.description}
            targetSelector={`[data-tour-item="${menu?.key}"]`}
            position={hoverHooks.tooltipData.position}
            offset={hoverHooks.tooltipData.offset}
            enabled={hoverTooltipsEnabled && !highlightedItemKey}
            isVisible={hoverHooks.isHovered}
          />
        )}
      </Flex>
    );
  };

  return (
    <StyledSidebarContainer
      padding={isSidebarOpen ? "16px" : "8px"}
      width={isSidebarOpen ? "270px" : "80px"}
      bg="#F3F4F6"
      display="flex"
      flexDirection="column"
      justifyContent="space-between" // pushes NavControl to bottom
      className={"sidebar-container"}
    >
      <SidebarHeader
        isDarkTheme={isDarkTheme}
        isOpen={isSidebarOpen}
        lg={lg}
        onNavigate={() => setSelectedItemKey("dashboard")}
      />
      {/* TOP SECTION */}
      <StyledSidebarSubBox>
        <Flex
          mx={"0px"}
          flexDirection={"column"}
          gap={"8px"}
          paddingTop={"15px"}
          flex={1}
          minHeight={0}
        >
          {/* {isSidebarOpen && <AddButton />} */}

          <Flex
            flexDir={"column"}
            alignItems={isSidebarOpen ? "stretch" : "center"}
          >
            {!isSidebarOpen && (
              <Flex flexDir={"column"} userSelect={"none"}>
                <Flex
                  alignItems={"center"}
                  width={"40px"}
                  height={"40px"}
                  borderRadius={"50%"}
                  justifyContent={"center"}
                  px={"0px"}
                  py={"0px"}
                  cursor={"pointer"}
                  onClick={toggleSidebar}
                  _hover={{
                    bg: "#EBEBEB",
                  }}
                >
                  <Flex alignItems={"center"} justifyContent={"center"}>
                    <Box color="#000000">
                      <CollapseIcon width={15} height={15} />
                    </Box>
                  </Flex>
                </Flex>
              </Flex>
            )}
            {sidebar?.map((menu: any, index: number) => {
              // If it's a regular item and its section is collapsed, don't render it
              if (
                !menu?.isHeading &&
                menu?.section &&
                collapsedSections?.[menu?.section]
              ) {
                return null;
              }
              return (
                <Box
                  key={index}
                  data-tour-item={menu?.key}
                  ref={highlightedItemKey === menu?.key ? undefined : undefined}
                >
                  <SidebarItem menu={menu} />
                </Box>
              );
            })}
          </Flex>

          {isSidebarOpen && isLoggedInUser?.roleId === USER_TYPES?.USER && (
            <Flex flexDir="column" flex={1} px="8px">
              <Flex
                alignItems="center"
                justifyContent="space-between"
                cursor="pointer"
                onClick={() =>
                  setIsConversationHistoryCollapsed(
                    !isConversationHistoryCollapsed
                  )
                }
                py="4px"
              >
                <Text fontSize="14px" fontWeight="500" color="#757575">
                  {t("sidebar.chatConversation")}
                </Text>
                {isConversationHistoryCollapsed ? (
                  <ChevronDownIcon boxSize={4} color="#757575" />
                ) : (
                  <ChevronUpIcon boxSize={4} color="#757575" />
                )}
              </Flex>
              {!isConversationHistoryCollapsed && (
                <Box flex={1}>
                  <ChatHistorySidebar
                    threads={threads?.length ? threads : []}
                    onChatClick={(chat: any) => handleChatClick({ chat })}
                    loading={chatHistoryLoading}
                    onRefetchThreads={refreshThreads}
                    isVisible={!isConversationHistoryCollapsed}
                  // maxHeight="100%"
                  />
                </Box>
              )}
            </Flex>
          )}
        </Flex>
      </StyledSidebarSubBox>

      <Flex justifyContent="center" width="100%">
        <LanguageToggle languages={languages} isSidebarOpen={isSidebarOpen} />
      </Flex>

      {/* BOTTOM SECTION */}
      <Flex flexDir={"column"} gap={"16px"}>
        <NavControl
          pathname={pathname}
          menuItemsList={menuItemsList}
          isDarkTheme={isDarkTheme}
          isOpen={isSidebarOpen}
          profileData={profileData}
          isMenuOpen={isMenuOpen}
          setIsMenuOpen={setIsMenuOpen}
          changePassword={updatePasswordOpen}
          setChangePassword={setUpdatePasswordOpen}
          logoutPopup={logoutPopup}
          setLogoutPopup={setLogoutPopup}
        />
      </Flex>
      <CustomPopup
        open={deleteAccountPopup}
        onClose={() => setDeleteAccountPopup(false)}
        title={t("preferences.signOutModalTitle")}
        children={
          <DeletePopupContent
            loading={loading}
            onDelete={deleteUserAccount}
            onCancel={() => setDeleteAccountPopup(false)}
          />
        }
      />

      <LogoutModal
        isOpen={logoutPopup}
        onClose={() => setLogoutPopup(false)}
        onConfirm={logoutHandler}
      />
    </StyledSidebarContainer>
  );
}
